import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    Calendar,
    BarChart3,
    UsersRound,
    Settings,
    LogOut,
    Sparkles,
    Menu,
    BookOpen,
    Euro,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";

const NAV = [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/dipendenti", label: "Dipendenti", icon: Users },
    { to: "/app/turni", label: "Turni", icon: Calendar },
    { to: "/app/performance", label: "Performance", icon: BarChart3 },
    { to: "/app/statistiche", label: "Statistiche", icon: Euro },
    { to: "/app/brigata", label: "Brigata", icon: UsersRound },
    { to: "/app/impostazioni", label: "Impostazioni", icon: Settings },
    { to: "/istruzioni", label: "Istruzioni", icon: BookOpen },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const tier = user?.subscription_tier || "free";
    const status = user?.subscription_status || "inactive";

    const onLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-sf-bg flex" data-testid="app-layout">
            {/* Sidebar */}
            <aside
                className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:sticky top-0 left-0 z-40 h-screen w-64 bg-white border-r border-sf transition-transform`}
                data-testid="app-sidebar"
            >
                <div className="px-2 py-2 border-b border-sf">
                    <Link to="/app" data-testid="sidebar-logo-link" className="flex items-center justify-center">
                        <Logo className="h-28 w-auto" />
                    </Link>
                </div>
                <nav className="p-3 flex flex-col gap-1">
                    {NAV.map(({ to, label, icon: Icon, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium lift ${
                                    isActive
                                        ? "bg-navy text-white"
                                        : "text-sf-secondary hover:bg-[#f0efeb]"
                                }`
                            }
                        >
                            <Icon size={18} />
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-sf bg-[#fafaf7]">
                    <div className="mb-3" data-testid="sidebar-plan-badge">
                        <div className="label-eyebrow mb-1">Piano</div>
                        <div className="flex items-center gap-2">
                            <span
                                className={`text-sm font-semibold capitalize ${
                                    status === "active"
                                        ? "text-navy"
                                        : "text-sf-muted"
                                }`}
                            >
                                {tier === "free" ? "Trial" : tier}
                            </span>
                            {status !== "active" && (
                                <Link
                                    to="/pricing"
                                    className="text-xs px-2 py-0.5 rounded bg-amber-brand text-navy font-bold lift"
                                    data-testid="sidebar-upgrade-cta"
                                >
                                    <Sparkles
                                        size={11}
                                        className="inline mr-1"
                                    />
                                    Upgrade
                                </Link>
                            )}
                        </div>
                    </div>
                    <div className="text-xs text-sf-muted mb-2">
                        {user?.name || user?.email}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={onLogout}
                        data-testid="logout-btn"
                    >
                        <LogOut size={14} className="mr-2" /> Esci
                    </Button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header
                    className="md:hidden glass-nav sticky top-0 z-30 px-4 py-3 flex items-center justify-between"
                    data-testid="mobile-header"
                >
                    <Logo className="h-20 w-auto" />
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        className="p-2 rounded-md border border-sf"
                    >
                        <Menu size={18} />
                    </button>
                </header>
                <main className="flex-1 p-6 md:p-10">{children}</main>
                <footer className="px-6 md:px-10 py-6 text-xs text-sf-muted border-t border-sf">
                    Staff Flow 360 è un prodotto di Makai · P.IVA 09364410969
                </footer>
            </div>

            {open && (
                <div
                    className="fixed inset-0 z-30 bg-black/30 md:hidden"
                    onClick={() => setOpen(false)}
                />
            )}
        </div>
    );
}
